import {
  AfterViewInit,
  Component,
  computed,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';

import {
  DialogCloseOptions,
  DialogCloseReason,
  injectDirtyCloseHandler,
  SuiDialogSimpleComponent,
  SuiDialogHeaderSlot,
  SuiDialogContentSlot,
  SuiDialogFooterSlot,
} from '@sui/dialog';

import { ViewPreset } from 'src/app/api/services/view-preset-api.service';
import { FormsService } from 'src/app/core/services/forms.service';
import { SimpleDialogBase } from 'src/app/shared/abstract/simple-dialog-base';

import { FormProperty } from 'src/app/core/models/form.models';
import { SuiButtonComponent } from '@sui/button';
import { DynamicFormComponent } from '../../../shared/components/dynamic-form/dynamic-form.component';
import { TranslatePipe } from '@ngx-translate/core';

interface DialogData {
  viewPreset?: ViewPreset;
}
interface DialogOutput {}

interface SaveAgGridSettingsPopupOutput {
  name: string;
  is_pinned: boolean;
  is_default?: boolean;
  uuid?: string;
}

@Component({
  selector: 'app-save-ag-grid-settings-dialog',
  templateUrl: './save-ag-grid-settings-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './save-ag-grid-settings-dialog.component.scss',
  imports: [
    SuiDialogSimpleComponent,
    SuiDialogHeaderSlot,
    SuiButtonComponent,
    SuiDialogContentSlot,
    DynamicFormComponent,
    FormsModule,
    ReactiveFormsModule,
    SuiDialogFooterSlot,
    TranslatePipe,
  ],
})
export class SaveAgGridSettingsDialogComponent
  extends SimpleDialogBase<DialogData, DialogOutput>
  implements AfterViewInit
{
  protected dirtyHandler = injectDirtyCloseHandler(this.dialogRef);
  protected formService = inject(FormsService);

  dialogTitle = this.translateService.instant(
    'save-ag-grid-settings-popup.title.untitled',
  );
  submitButtonText = this.translateService.instant('btn.create');

  controls: Record<string, FormControl> = {};
  public formProperties: FormProperty[] = [
    {
      propertyName: 'settings_name',
      controlType: 'text',
      validators: [Validators.required],
      propertyLabel: this.translateService.instant(
        'save-ag-grid-settings-popup.popup-title.save-settings',
      ),
      additionalConfig: {
        palceholder: this.translateService.instant(
          'save-ag-grid-settings-popup.settings-name-input.placeholder',
        ),
      },
    },

    {
      propertyName: 'is_pinned',
      controlType: 'checkbox',
      validators: [],
      propertyLabel: this.translateService.instant(
        'save-ag-grid-settings-popup.form-label.is-pinned',
      ),
    },
    {
      propertyName: 'is_default',
      controlType: 'checkbox',
      validators: [],
      propertyLabel: this.translateService.instant(
        'save-ag-grid-settings-popup.form-label.is-default',
      ),
    },
  ];

  protected formGroup = this.formService.createFormGroup(this.formProperties);

  private formValueChanges = toSignal(this.formGroup.valueChanges, {
    requireSync: false,
  });
  private formStatusChanges = toSignal(this.formGroup.statusChanges, {
    initialValue: this.formGroup.status,
  });

  public isDirty = computed(() => {
    this.formValueChanges();
    return this.formGroup.dirty;
  });

  public isValid = computed(() => {
    return this.formStatusChanges() === 'VALID';
  });

  get viewPreset() {
    return this.dialogData.viewPreset;
  }

  ngOnInit() {
    if (this.viewPreset) {
      this.dialogTitle = this.viewPreset.name;
      this.submitButtonText = this.translateService.instant('btn.update');
    }

    const data = {
      settings_name: this.viewPreset?.name || '',
      is_pinned: this.viewPreset?.is_pinned ?? false,
      is_default: this.viewPreset?.is_default ?? false,
    };

    this.formService.populateFormValues(this.formGroup, data);
  }

  ngAfterViewInit(): void {
    this.setupDirtyHandler(this.isDirty, this.isValid);
  }

  protected override performSave(): DialogCloseOptions<DialogOutput> {
    const output: SaveAgGridSettingsPopupOutput = {
      name: this.formGroup.get('settings_name')?.value ?? '',
      is_pinned: this.formGroup.get('is_pinned')?.value ?? false,
      is_default: this.formGroup.get('is_default')?.value ?? false,
      uuid: this.viewPreset ? this.viewPreset.uuid : undefined,
    };

    return {
      reason: DialogCloseReason.Save,
      data: output,
    };
  }

  save(): void {
    if (!this.isValid()) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const result = this.performSave();
    this.dirtyHandler.closeImmediately(result);
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
